import { AloftDate, syntaxFNMA } from '@aloft/toolkit-shared';
import { Block, Flex } from '@aloft/toolkit-sui';

import { memo, useEffect, useMemo, useRef } from 'react';

import { PropertyGrid } from '../../components/PropertyGrid/PropertyGrid.tsx';
import { useTkv2ReferenceBarEvents } from '../../components/ReferenceBar/useTkv2ReferenceBarEvents.ts';
import { useToaster } from '../../hooks/useToaster/useToaster.tsx';
import { selectCompSequence, selectEffectiveDate } from '../../redux/features/selectors/orderSelectors.ts';
import { selectPropertyIdentificationById } from '../../redux/features/selectors/propertyDataSelectors/propertyIdentificationSelectors.ts';
import { selectSaleDateById } from '../../redux/features/selectors/propertyDataSelectors/transactionSelectors.ts';
import { useAppSelector } from '../../redux/hooks.ts';
import { FeatureGridDataSourcesSection } from './columnSections/FeatureGridDataSourcesSection.tsx';
import { FeatureGridFeatureSections } from './columnSections/FeatureGridFeatureSections.tsx';
import { FeatureGridMainDwellingSection } from './columnSections/FeatureGridMainDwellingSection.tsx';
import { FeatureGridSiteSection } from './columnSections/FeatureGridSiteSection.tsx';
import { FeatureGridTransactionSection } from './columnSections/FeatureGridTransactionSection.tsx';
import { FeatureGridLabelColumnHeader } from './components/FeatureGridLabelColumnHeader.tsx';
import { FeatureGridToolbar } from './components/FeatureGridToolbar.tsx';
import { LocationType } from '../../utils/locationTypeProximityRequirementMiles.ts';
import {
  formatSaleDateRequirementTooltip,
  getLocationTypeSaleDateRequirementMonths,
  isSaleDateOutOfBounds,
} from '../../utils/locationTypeSaleDateRequirementMonths.ts';

export const FeatureGridModule = memo(() => {
  const toast = useToaster();
  const lastToastKeyRef = useRef<string | undefined>(undefined);
  const compSequence = useAppSelector(selectCompSequence);
  const effectiveDate = useAppSelector(selectEffectiveDate);
  const subjectPropertyIdentification = useAppSelector((state) =>
    selectPropertyIdentificationById(state, 'subject'),
  );
  const locationType = subjectPropertyIdentification?.locationType as LocationType | undefined;
  const compSaleDates = useAppSelector((state) =>
    compSequence.map((compId) => ({
      compId,
      saleDate: selectSaleDateById(state, compId),
    })),
  );
  const columnSections = useMemo(
    () => [
      FeatureGridDataSourcesSection,
      FeatureGridTransactionSection,
      FeatureGridSiteSection,
      FeatureGridMainDwellingSection,
      FeatureGridFeatureSections,
    ],
    [],
  );
  const saleDateRequirementMonths = getLocationTypeSaleDateRequirementMonths(locationType);

  useEffect(() => {
    if (!locationType || saleDateRequirementMonths === undefined || !effectiveDate) {
      lastToastKeyRef.current = undefined;
      return;
    }

    const effectiveAloftDate = AloftDate.schema.parse(effectiveDate);
    const outOfBoundsCompIds = compSaleDates
      .filter(({ saleDate }) => {
        if (!saleDate) return false;
        try {
          const saleAloftDate = syntaxFNMA.isoMonthToAloftDate(saleDate as syntaxFNMA.IsoMonth);
          return isSaleDateOutOfBounds(saleAloftDate, effectiveAloftDate, saleDateRequirementMonths);
        } catch {
          return false;
        }
      })
      .map(({ compId }) => compId);

    const nextToastKey = `${locationType}:${saleDateRequirementMonths}:${outOfBoundsCompIds.join(',')}`;
    if (outOfBoundsCompIds.length === 0) {
      lastToastKeyRef.current = undefined;
      return;
    }
    if (lastToastKeyRef.current === nextToastKey) return;

    lastToastKeyRef.current = nextToastKey;
    toast({
      id: `sale-date-warning-${nextToastKey}`,
      type: 'warning',
      title: 'Sale Date Warning',
      body: formatSaleDateRequirementTooltip(locationType, saleDateRequirementMonths),
    });
  }, [compSaleDates, effectiveDate, locationType, saleDateRequirementMonths, toast]);

  return (
    <Flex
      {...useTkv2ReferenceBarEvents()}
      flexDirection="column"
      overflow="hidden"
      width="100%"
      gap="8px"
    >
      <FeatureGridToolbar />
      <Block overflow="auto" paddingRight="10px">
        <PropertyGrid
          gridType="FeatureGrid"
          columnSections={columnSections}
          labelColumnHeader={<FeatureGridLabelColumnHeader />}
        />
      </Block>
    </Flex>
  );
});
